import type { AttachmentSummary } from './types';

/**
 * Feedback 1 #7: inline image attachments (chat, threads, DMs, announcements, kudos). SVG and anything else stays a
 * file card: SVG can carry script and imgproxy doesn't rasterise it.
 */
export const isInlineImage = (contentType: string | null | undefined) => /^image\/(png|jpe?g|gif|webp|avif)$/i.test(contentType ?? '');

export const splitAttachments = <T extends Pick<AttachmentSummary, 'content_type'>>(items: T[]) =>
  ({ images: items.filter(a => isInlineImage(a.content_type)), files: items.filter(a => !isInlineImage(a.content_type)) });

/** width / height, clamped so a panorama or a receipt photo still makes a sensible tile; 4:3 when unknown. */
export function aspectRatio(a: { width?: number | null; height?: number | null }, min = 0.5, max = 2.4): number {
  if (!a.width || !a.height) return 4 / 3;
  return Math.min(max, Math.max(min, a.width / a.height));
}

/** Grid for n images: up to 4 tiles, the last shows "+N" for the rest. */
export function imageGrid(n: number): { tiles: number; more: number; cols: 1 | 2 } {
  if (n <= 1) return { tiles: n, more: 0, cols: 1 };
  return { tiles: Math.min(n, 4), more: Math.max(0, n - 4), cols: 2 };
}

/** Thumbnail width to ask the server for, by the tile's rendered width and screen density. */
export function thumbWidth(cssPx: number, dpr = 1): 320 | 640 | 1280 {
  const px = cssPx * Math.min(dpr, 3);
  return px <= 320 ? 320 : px <= 640 ? 640 : 1280;
}
